<template>
  <div class="provider-instructions-component" :class="{ terminal }">
    <div
      class="instructions-container"
      :class="{
        'px-4': terminal,
        'pt-3': terminal,
        'pb-4': terminal,
        'font-monospace': terminal,
        'elevation-10': terminal,
        'close-animation': closeAnimation,
      }"
    >
      <div v-if="terminal" class="useless-rounds pb-3">
        <div v-for="n in 3" :key="n" class="useless-round" @click="n === 1 && onClose()" />
      </div>

      <div v-if="provider === 'baremetal'">
        <p>
          1- Install PM2 Runtime on your server:
        </p>
        <CodeExtract prompt="$" :checked="serverLinked || appLinked">
          npm install -g pm2
        </CodeExtract>

        <p>
          2- Then link PM2 to PM2.io:
        </p>
        <CodeExtract prompt="$" :checked="serverLinked">
          {{ onPremiseEnv }}{{ localPremiseDevEnv }}pm2 link {{ secretId }} {{ publicId }}
        </CodeExtract>

        <p>
          3- You're set. Now start a Node.js application:
        </p>
        <CodeExtract prompt="$" :checked="appLinked">
          pm2 start app.js
        </CodeExtract>
      </div>

      <div v-else-if="provider === 'docker'">
        <p>
          Edit your Dockerfile and replace the <code class="base px-1">node</code> by
          <code class="base px-1">pm2-runtime</code>:
        </p>
        <!-- prettier-ignore -->
        <CodeExtract>
RUN npm install pm2 -g
ENV PM2_PUBLIC_KEY {{ publicId }}
ENV PM2_SECRET_KEY {{ secretId }}
{{ onPremiseConfigDocker }}
CMD ["pm2-runtime", "app.js"]
        </CodeExtract>
        <p>
          Finally, start the container.
        </p>
      </div>

      <div v-else-if="provider === 'ebs'">
        <p>
          First, add pm2 as a dependency:
        </p>
        <CodeExtract prompt="$">
          npm install pm2
        </CodeExtract>

        <p>
          Edit your package.json:
        </p>
        <!-- prettier-ignore -->
        <CodeExtract>
"scripts": {
  "prod:start": "pm2-runtime app.js"
}
        </CodeExtract>

        <p>
          Set the env variables:
        </p>
        <CodeExtract prompt="$">
PM2_PUBLIC_KEY={{ publicId }}
PM2_SECRET_KEY={{ secretId }}
        </CodeExtract>

        <p>
          Finally, deploy your application.
        </p>
      </div>

      <div v-else-if="provider === 'heroku'">
        <p>
          First, add pm2 as a dependency:
        </p>
        <CodeExtract prompt="$">
          npm install pm2
        </CodeExtract>

        <p>
          Edit your package.json:
        </p>
        <!-- prettier-ignore -->
        <CodeExtract>
"scripts": {
  "start": "pm2-runtime app.js"
}
        </CodeExtract>

        <p>
          Set the env variables:
        </p>
        <!-- prettier-ignore -->
        <CodeExtract prompt="$">
          heroku config:set {{ onPremiseEnv }}{{ localPremiseDevEnv }}PM2_PUBLIC_KEY={{ publicId }} PM2_SECRET_KEY={{ secretId }}
        </CodeExtract>

        <p>
          Finally, deploy your application.
        </p>
      </div>
    </div>
    <div v-if="terminal" class="egg-message">
      Oh hi, Mark!
    </div>
  </div>
</template>

<script>
import config from '@/config/constants'
import runDelayedMixin from '@/mixins/runDelayedMixin'
import CodeExtract from '@/components/CodeExtract'

export default {
  name: 'ProviderInstructions',

  components: {
    CodeExtract,
  },

  mixins: [runDelayedMixin],

  props: {
    provider: {
      type: String,
      required: true,
    },
    terminal: {
      type: Boolean,
    },
  },

  data() {
    return {
      serverLinked: false,
      appLinked: false,
      closeAnimation: false,
    }
  },

  computed: {
    onPremiseConfigDocker() {
      return config.DEDICATED ? `ENV KEYMETRICS_NODE ${window.location.origin}` : ''
    },
    localPremiseDevEnv() {
      if (window.location.hostname == 'cl1.km.io') {
        return 'KEYMETRICS_NODE=http://cl1.km.io:3000 '
      }
      return ''
    },
    onPremiseEnv() {
      return config.DEDICATED ? `KEYMETRICS_NODE=${window.location.origin} ` : ''
    },

    bucketState() {
      return this.$store.getters.currentBucket
    },

    secretId() {
      return this.bucketState.bucket.secret_id
    },

    publicId() {
      return this.bucketState.bucket.public_id
    },
  },

  mounted() {
    this.$watch(
      () => {
        const selection = this.$store.getters.select({ appName: false })
        return {
          servers: selection.servers.length,
          apps: selection.appNames.length,
        }
      },
      (next, previous) => {
        if (next.servers > previous.servers) {
          this.serverLinked = true
          this.runDelayed(
            'servers',
            () => {
              this.serverLinked = false
            },
            60e3,
          )
        }
        if (next.apps > previous.apps) {
          this.appLinked = true
          this.runDelayed(
            'servers',
            () => {
              this.appLinked = false
            },
            60e3,
          )
        }
      },
    )
  },

  methods: {
    onClose() {
      this.closeAnimation = true
      this.runDelayed(
        'closeAnimation',
        () => {
          this.closeAnimation = false
        },
        3e3,
      )
    },
  },
}
</script>

<style lang="postcss" scoped>
.provider-instructions-component {
  position: relative;
  font-size: 17px !important;
  &.terminal {
    & .instructions-container {
      position: relative;
      z-index: 1;
      background-color: var(--base);
      border-radius: 5px;
    }

    & .useless-rounds {
      display: flex;
      padding-top: 4px;

      & .useless-round {
        width: 16px;
        height: 16px;
        margin-right: 11px;
        border-radius: 50%;

        &:nth-child(1) {
          cursor: pointer;
          background-color: #ff5f56;
          transition: background-color 0.4s;
          &:hover {
            background-color: rgba(255, 95, 86, 0.8);
          }
        }
        &:nth-child(2) {
          background-color: #ffbd2e;
        }
        &:nth-child(3) {
          background-color: #27c93f;
        }
      }
    }

    & p {
      &::before {
        content: '#';
      }
      opacity: 0.6;
      &:not(:first-child) {
        margin-top: 24px;
      }
    }
  }

  &:not(.terminal) {
    & p {
      margin-bottom: 8px;
    }
    & .code-extract-component {
      margin-bottom: 16px;
      padding: 8px;
    }
  }
}

.egg-message {
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 48px;
  color: var(--base);
  font-family: var(--font-stylized);
}

a {
  text-decoration: none;
  color: white;
}

.info-link {
  text-decoration: underline;
  color: var(--primary);
}

pre {
  white-space: pre-wrap;
}

pre.copiable {
  &:hover {
    cursor: pointer;
  }
}

p {
  margin: 0;
}

.hover-copy {
  opacity: 0.4;
  &:hover {
    opacity: 1;
  }
}

.pragma-instructions {
  display: flex;
  & .block {
    padding: 0 10px;
    width: 33%;
    border-right: 1px dotted #666;
    height: 380px;
    overflow-y: auto;
  }
}

.close-animation {
  animation: 3s ease-out close-animation;
  transform-origin: 0 0;
}
@keyframes close-animation {
  0% {
    transform: rotate(0deg);
  }
  10% {
    transform: rotate(60deg);
  }
  20% {
    transform: rotate(40deg);
  }
  30% {
    transform: rotate(54deg);
  }
  40% {
    transform: rotate(42deg);
  }
  50% {
    transform: translateY(0) rotate(46deg);
  }
  70% {
    transform: translateY(800px) rotate(46deg);
  }
  90% {
    transform: translateY(800px) rotate(46deg);
  }
}
</style>
